import React, { useState, useEffect } from 'react';
import { supabase } from '../../lib/supabase';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import Card from '../../components/ui/Card';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiArrowUp, FiArrowDown } = FiIcons;

export default function Services() {
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [newService, setNewService] = useState({ name: '', base_price: '', icon_name: 'Feather' });

  const fetchServices = async () => {
    const { data } = await supabase
      .from('services')
      .select('*')
      .order('display_order', { ascending: true })
      .order('created_at', { ascending: true });
      
    if (data) {
      // Normalize display_orders locally in case they are newly added or 0
      const normalized = data.map((s, idx) => ({ ...s, display_order: idx }));
      setServices(normalized);
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchServices();
  }, []);

  const handleAdd = async (e) => {
    e.preventDefault();
    const { error } = await supabase.from('services').insert([
      { ...newService, display_order: services.length }
    ]);
    if (!error) {
      setShowAdd(false);
      setNewService({ name: '', base_price: '', icon_name: 'Feather' });
      fetchServices();
    }
  };

  const moveService = async (index, direction) => {
    if (direction === 'up' && index === 0) return;
    if (direction === 'down' && index === services.length - 1) return;

    const newServices = [...services];
    const targetIndex = direction === 'up' ? index - 1 : index + 1;

    // Swap elements
    [newServices[index], newServices[targetIndex]] = [newServices[targetIndex], newServices[index]];

    // Update display_order for all to match their new array index
    const updatedServices = newServices.map((s, i) => ({ ...s, display_order: i }));
    setServices(updatedServices); // Optimistic UI update

    // Save to DB
    for (const s of updatedServices) {
      await supabase.from('services').update({ display_order: s.display_order }).eq('id', s.id);
    }
  };

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold text-brand-navy">Service Management</h1>
        <Button onClick={() => setShowAdd(!showAdd)} variant="accent" size="sm">
          {showAdd ? 'Cancel' : '+ Add Service'}
        </Button>
      </div>

      {showAdd && (
        <Card className="mb-8 border-brand-gold/30 bg-brand-gold/5">
          <form onSubmit={handleAdd} className="grid grid-cols-1 md:grid-cols-3 gap-4 items-end">
            <Input 
              label="Service Name" 
              required 
              value={newService.name} 
              onChange={e => setNewService({ ...newService, name: e.target.value })} 
            />
            <Input 
              label="Base Price (₹)" 
              type="number" 
              required 
              value={newService.base_price} 
              onChange={e => setNewService({ ...newService, base_price: e.target.value })} 
            />
            <Button type="submit">Create Service</Button>
          </form>
        </Card>
      )}

      <div className="grid grid-cols-1 gap-4 max-w-3xl">
        {services.map((service, index) => (
          <Card key={service.id} className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="h-10 w-10 bg-brand-light rounded-full flex items-center justify-center text-brand-navy shrink-0">
                <SafeIcon name={service.icon_name} />
              </div>
              <div>
                <h3 className="font-bold flex items-center gap-2">
                  {service.name}
                  <div className={`h-2 w-2 rounded-full ${service.is_active ? 'bg-green-500' : 'bg-red-500'}`} />
                </h3>
                <p className="text-sm text-gray-500">₹{service.base_price}</p>
              </div>
            </div>
            
            {/* Reorder Controls */}
            <div className="flex items-center gap-2 bg-gray-50 p-1 rounded-lg border border-gray-100">
              <button 
                onClick={() => moveService(index, 'up')}
                disabled={index === 0}
                className="p-2 rounded hover:bg-white disabled:opacity-30 disabled:hover:bg-transparent transition-colors text-brand-navy"
                title="Move Up"
              >
                <FiArrowUp />
              </button>
              <div className="w-px h-6 bg-gray-200"></div>
              <button 
                onClick={() => moveService(index, 'down')}
                disabled={index === services.length - 1}
                className="p-2 rounded hover:bg-white disabled:opacity-30 disabled:hover:bg-transparent transition-colors text-brand-navy"
                title="Move Down"
              >
                <FiArrowDown />
              </button>
            </div>
          </Card>
        ))}
      </div>
    </div>
  );
}